import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../api';

function parseMessage(text) {
  if (!text) return { _type: 'text', body: '' };
  if (text.startsWith('{')) {
    try { return JSON.parse(text); } catch(e) {}
  }
  return { _type: 'text', body: text };
}

function useMediaBlob(mediaId) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (!mediaId || fetched.current) return;
    fetched.current = true;
    setLoading(true);
    api.get('/messages/media/' + mediaId, { responseType: 'blob' })
      .then(r => setBlobUrl(URL.createObjectURL(r.data)))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [mediaId]);

  return { blobUrl, loading, error };
}

function ImageMsg({ parsed }) {
  const { blobUrl, loading, error } = useMediaBlob(parsed.media_id);
  if (loading) return <div style={{color:'#aaa',fontSize:'12px',padding:'4px'}}>Loading image…</div>;
  if (error || !blobUrl) return <span style={{color:'#888',fontSize:'12px'}}>📷 Image (unable to load)</span>;
  return (
    <div>
      <a href={blobUrl} target="_blank" rel="noreferrer">
        <img src={blobUrl} alt="media" style={{maxWidth:'220px',maxHeight:'220px',borderRadius:'6px',display:'block',cursor:'pointer'}} />
      </a>
      {parsed.caption && <div style={{fontSize:'12px',marginTop:'4px',color:'#555'}}>{parsed.caption}</div>}
    </div>
  );
}

function VideoMsg({ parsed }) {
  const { blobUrl, loading, error } = useMediaBlob(parsed.media_id);
  if (loading) return <div style={{color:'#aaa',fontSize:'12px',padding:'4px'}}>Loading video…</div>;
  if (error || !blobUrl) return <span style={{color:'#888',fontSize:'12px'}}>🎥 Video (unable to load)</span>;
  return (
    <div>
      <video controls style={{maxWidth:'240px',borderRadius:'6px',display:'block'}}>
        <source src={blobUrl} type={parsed.mime_type || 'video/mp4'} />
      </video>
      {parsed.caption && <div style={{fontSize:'12px',marginTop:'4px'}}>{parsed.caption}</div>}
    </div>
  );
}

function AudioMsg({ parsed }) {
  const { blobUrl, loading, error } = useMediaBlob(parsed.media_id);
  if (loading) return <div style={{color:'#aaa',fontSize:'12px',padding:'4px'}}>Loading…</div>;
  if (error || !blobUrl) return <span style={{color:'#888',fontSize:'12px'}}>{parsed.voice ? '🎤 Voice note' : '🎵 Audio'} (unable to load)</span>;
  return (
    <div>
      <audio controls style={{maxWidth:'220px'}}>
        <source src={blobUrl} type={parsed.mime_type || 'audio/ogg'} />
      </audio>
      <div style={{fontSize:'11px',color:'#888',marginTop:'2px'}}>{parsed.voice ? '🎤 Voice note' : '🎵 Audio'}</div>
    </div>
  );
}

function DocumentMsg({ parsed }) {
  const { blobUrl, loading, error } = useMediaBlob(parsed.media_id);
  if (loading) return <div style={{color:'#aaa',fontSize:'12px',padding:'4px'}}>Loading…</div>;
  if (error || !blobUrl) return <span style={{color:'#888',fontSize:'12px'}}>📄 {parsed.filename || 'Document'} (unable to load)</span>;
  return (
    <div>
      <a href={blobUrl} download={parsed.filename || 'document'}
        style={{color:'#1a73e8',fontSize:'13px',display:'flex',alignItems:'center',gap:'6px',textDecoration:'none'}}>
        <span style={{fontSize:'20px'}}>📄</span>
        <span style={{textDecoration:'underline'}}>{parsed.filename || 'Download document'}</span>
      </a>
      {parsed.caption && <div style={{fontSize:'12px',marginTop:'4px',color:'#555'}}>{parsed.caption}</div>}
    </div>
  );
}

function MediaMessage({ parsed }) {
  if (parsed._type === 'text') return <span style={{whiteSpace:'pre-line',lineHeight:'1.6'}}>{parsed.body}</span>;
  if (parsed._type === 'image') return <ImageMsg parsed={parsed} />;
  if (parsed._type === 'video') return <VideoMsg parsed={parsed} />;
  if (parsed._type === 'audio') return <AudioMsg parsed={parsed} />;
  if (parsed._type === 'document') return <DocumentMsg parsed={parsed} />;
  if (parsed._type === 'location') {
    const mapsUrl = `https://maps.google.com/?q=${parsed.latitude},${parsed.longitude}`;
    return <a href={mapsUrl} target="_blank" rel="noreferrer" style={{color:'#1a73e8',fontSize:'13px'}}>📍 {parsed.name || 'Location'}{parsed.address ? ` — ${parsed.address}` : ''}</a>;
  }
  if (parsed._type === 'contacts') return <span style={{fontSize:'13px'}}>👤 {parsed.name}{parsed.phone ? ` (${parsed.phone})` : ''}</span>;
  if (parsed._type === 'reaction') return <span style={{fontSize:'20px'}}>{parsed.emoji}</span>;
  return <span style={{color:'#aaa',fontSize:'12px',fontStyle:'italic'}}>📎 {parsed._type || 'Unsupported message'}</span>;
}

// ── File preview before sending ──────────────────────────────
function AttachPreview({ file, caption, onCaptionChange, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!file) return;
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  if (!file) return null;
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');

  return (
    <div style={{
      position:'absolute', bottom:'70px', left:'16px', right:'16px',
      background:'#fff', border:'1px solid #ddd', borderRadius:'12px',
      padding:'14px', boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:10
    }}>
      <div style={{display:'flex',alignItems:'flex-start',gap:'12px'}}>
        {/* Preview thumbnail */}
        <div style={{flexShrink:0}}>
          {isImage && previewUrl && (
            <img src={previewUrl} alt="preview" style={{width:'80px',height:'80px',objectFit:'cover',borderRadius:'8px'}} />
          )}
          {isVideo && previewUrl && (
            <video src={previewUrl} style={{width:'80px',height:'80px',objectFit:'cover',borderRadius:'8px'}} muted />
          )}
          {!isImage && !isVideo && (
            <div style={{width:'80px',height:'80px',background:'#f0f0f0',borderRadius:'8px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'4px'}}>
              <span style={{fontSize:'28px'}}>{file.type.startsWith('audio/') ? '🎵' : '📄'}</span>
              <span style={{fontSize:'9px',color:'#888',textAlign:'center',wordBreak:'break-all',padding:'0 4px'}}>
                {file.name.length > 15 ? file.name.substring(0,12)+'...' : file.name}
              </span>
            </div>
          )}
        </div>

        {/* File info + caption */}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:'12px',fontWeight:600,marginBottom:'4px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            {file.name}
          </div>
          <div style={{fontSize:'11px',color:'#aaa',marginBottom:'8px'}}>
            {(file.size / 1024).toFixed(0)} KB · {file.type}
          </div>
          <input
            value={caption}
            onChange={e => onCaptionChange(e.target.value)}
            placeholder="Add a caption (optional)…"
            style={{width:'100%',padding:'7px 10px',border:'1px solid #eee',borderRadius:'8px',fontSize:'12px',outline:'none',boxSizing:'border-box'}}
          />
        </div>

        {/* Remove button */}
        <button onClick={onRemove} style={{background:'none',border:'none',cursor:'pointer',fontSize:'18px',color:'#aaa',padding:'0',flexShrink:0,lineHeight:1}}>✕</button>
      </div>
    </div>
  );
}

export default function Inbox() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const [attachCaption, setAttachCaption] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const fetchInbox = useCallback(() => {
    api.get('/messages/inbox').then(r => {
      const grouped = {};
      r.data.forEach(m => {
        if (!grouped[m.from_phone]) {
          grouped[m.from_phone] = { phone: m.from_phone, messages: [], unread: 0, last_message: '', last_time: '' };
        }
        grouped[m.from_phone].messages.push(m);
        if (!m.is_read && m.type !== 'outgoing') grouped[m.from_phone].unread++;
        if (!grouped[m.from_phone].last_time || new Date(m.received_at) > new Date(grouped[m.from_phone].last_time)) {
          let preview = '';
          if (m.message_text) {
            const parsed = parseMessage(m.message_text);
            if (parsed._type === 'text') preview = parsed.body.replace(/\n/g, ' ').substring(0, 45);
            else if (parsed._type === 'image') preview = '📷 Image' + (parsed.caption ? ': ' + parsed.caption : '');
            else if (parsed._type === 'video') preview = '🎥 Video';
            else if (parsed._type === 'audio') preview = parsed.voice ? '🎤 Voice note' : '🎵 Audio';
            else if (parsed._type === 'document') preview = '📄 ' + (parsed.filename || 'Document');
            else if (parsed._type === 'location') preview = '📍 Location';
            else if (parsed._type === 'contacts') preview = '👤 ' + (parsed.name || 'Contact');
            else if (parsed._type === 'reaction') preview = parsed.emoji;
            else preview = parsed._type || '';
          }
          grouped[m.from_phone].last_message = (m.type === 'outgoing' ? 'You: ' : '') + preview;
          grouped[m.from_phone].last_time = m.received_at;
        }
      });
      Object.values(grouped).forEach(c => c.messages.sort((a, b) => new Date(a.received_at) - new Date(b.received_at)));
      const list = Object.values(grouped).sort((a, b) => new Date(b.last_time) - new Date(a.last_time));
      setConversations(list);
      if (selected) {
        const updated = list.find(c => c.phone === selected.phone);
        if (updated) { setMessages(updated.messages); setTimeout(scrollToBottom, 100); }
      }
    });
  }, [selected]);

  useEffect(() => {
    fetchInbox();
    const interval = setInterval(fetchInbox, 10000);
    return () => clearInterval(interval);
  }, [fetchInbox]);

  useEffect(() => { setTimeout(scrollToBottom, 100); }, [messages]);

  const openConversation = async (conv) => {
    setSelected(conv);
    setMessages(conv.messages);
    for (const m of conv.messages) {
      if (!m.is_read && m.type !== 'outgoing') await api.post('/messages/inbox/read/' + m.id);
    }
    fetchInbox();
    setTimeout(scrollToBottom, 200);
  };

  const sendReply = async () => {
    if (attachFile) {
      await sendMedia();
      return;
    }
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post('/messages/inbox/reply', { phone: selected.phone, message: reply });
      setReply('');
      fetchInbox();
    } catch(e) {
      alert('Failed to send: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  const sendMedia = async () => {
    if (!attachFile || !selected) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', attachFile);
      formData.append('phone', selected.phone);
      if (attachCaption.trim()) formData.append('caption', attachCaption.trim());
      await api.post('/messages/inbox/reply-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachFile(null);
      setAttachCaption('');
      fetchInbox();
    } catch(e) {
      alert('Failed to send media: ' + (e.response?.data?.error || e.message));
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 16 * 1024 * 1024) { alert('File too large. Max 16MB allowed.'); return; }
    setAttachFile(file);
    setAttachCaption('');
    e.target.value = '';
  };

  const getTicks = (m) => {
    if (m.type !== 'outgoing') return null;
    if (m.status === 'read') return <span style={{color:'#53bdeb',fontSize:'11px'}}> ✓✓</span>;
    if (m.status === 'delivered') return <span style={{color:'#999',fontSize:'11px'}}> ✓✓</span>;
    return <span style={{color:'#999',fontSize:'11px'}}> ✓</span>;
  };

  const filtered = conversations.filter(c =>
    c.phone.includes(search) || c.last_message?.toLowerCase().includes(search.toLowerCase())
  );
  const totalUnread = conversations.reduce((sum, c) => sum + c.unread, 0);

  return (
    <div style={{display:'flex',height:'calc(100vh - 120px)',background:'#fff',borderRadius:'10px',overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}>

      {/* Left Panel */}
      <div style={{width:'320px',borderRight:'1px solid #f0f0f0',display:'flex',flexDirection:'column',flexShrink:0}}>
        <div style={{padding:'16px',borderBottom:'1px solid #f0f0f0'}}>
          <div style={{display:'flex',alignItems:'center',marginBottom:'12px'}}>
            <h3 style={{margin:0,fontSize:'15px'}}>
              Inbox
              {totalUnread > 0 && <span style={{background:'#25d366',color:'#fff',borderRadius:'20px',padding:'2px 8px',fontSize:'11px',marginLeft:'8px'}}>{totalUnread}</span>}
            </h3>
            <span style={{marginLeft:'auto',fontSize:'11px',color:'#aaa'}}>↻ 10s</span>
          </div>
          <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
            style={{width:'100%',padding:'8px 12px',border:'1px solid #eee',borderRadius:'8px',fontSize:'13px',outline:'none'}} />
        </div>
        <div style={{overflowY:'auto',flex:1}}>
          {filtered.length === 0 && <div style={{padding:'32px',textAlign:'center',color:'#aaa',fontSize:'13px'}}>No conversations yet</div>}
          {filtered.map(conv => (
            <div key={conv.phone} onClick={() => openConversation(conv)} style={{
              padding:'14px 16px',cursor:'pointer',borderBottom:'1px solid #f5f5f5',
              background: selected?.phone === conv.phone ? '#f0f7ff' : conv.unread > 0 ? '#fffde7' : '#fff',
              borderLeft: selected?.phone === conv.phone ? '3px solid #1a73e8' : '3px solid transparent'
            }}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                <span style={{fontWeight:600,fontSize:'13px'}}>+{conv.phone}</span>
                <span style={{fontSize:'11px',color:'#aaa'}}>{conv.last_time ? new Date(conv.last_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}) : ''}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:'12px',color:'#666',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'220px'}}>{conv.last_message}</span>
                {conv.unread > 0 && <span style={{background:'#25d366',color:'#fff',borderRadius:'50%',width:'18px',height:'18px',fontSize:'10px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{conv.unread}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel */}
      {!selected ? (
        <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:'8px',color:'#aaa'}}>
          <div style={{fontSize:'48px'}}>💬</div>
          <div style={{fontSize:'14px'}}>Select a conversation</div>
        </div>
      ) : (
        <div style={{flex:1,display:'flex',flexDirection:'column',minWidth:0}}>
          {/* Header */}
          <div style={{padding:'14px 20px',borderBottom:'1px solid #f0f0f0',display:'flex',alignItems:'center',gap:'12px',background:'#f8f9fa',flexShrink:0}}>
            <div style={{width:'38px',height:'38px',borderRadius:'50%',background:'#1a73e8',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:'14px',flexShrink:0}}>
              {selected.phone.slice(-2)}
            </div>
            <div>
              <div style={{fontWeight:600,fontSize:'14px'}}>+{selected.phone}</div>
              <div style={{fontSize:'11px',color:'#888'}}>{messages.length} messages</div>
            </div>
          </div>

          {/* Messages */}
          <div style={{flex:1,overflowY:'auto',padding:'16px',display:'flex',flexDirection:'column',gap:'8px',background:'#e5ddd5'}}>
            {messages.map(m => {
              const isOutgoing = m.type === 'outgoing';
              const parsed = parseMessage(m.message_text);
              return (
                <div key={m.id} style={{display:'flex',justifyContent: isOutgoing ? 'flex-end' : 'flex-start'}}>
                  <div style={{
                    background: isOutgoing ? '#dcf8c6' : '#fff',
                    padding:'10px 14px',
                    borderRadius: isOutgoing ? '8px 2px 8px 8px' : '2px 8px 8px 8px',
                    maxWidth:'68%',boxShadow:'0 1px 2px rgba(0,0,0,0.1)',fontSize:'13px',wordBreak:'break-word'
                  }}>
                    <MediaMessage parsed={parsed} />
                    <div style={{fontSize:'10px',color:'#999',marginTop:'6px',textAlign:'right',display:'flex',alignItems:'center',justifyContent:'flex-end',gap:'2px'}}>
                      <span>{new Date(m.received_at).toLocaleString('en-IN',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}</span>
                      {getTicks(m)}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Box */}
          <div style={{position:'relative',flexShrink:0}}>
            {/* Attach preview popup */}
            <AttachPreview
              file={attachFile}
              caption={attachCaption}
              onCaptionChange={setAttachCaption}
              onRemove={() => { setAttachFile(null); setAttachCaption(''); }}
            />

            <div style={{padding:'12px 16px',borderTop:'1px solid #ddd',display:'flex',gap:'8px',background:'#f0f0f0',alignItems:'center'}}>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                style={{display:'none'}}
                onChange={handleFileSelect}
              />

              {/* Attach button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                title="Attach file"
                style={{
                  background: attachFile ? '#1a73e8' : '#fff',
                  color: attachFile ? '#fff' : '#666',
                  border:'1px solid #ddd',
                  borderRadius:'50%',width:'38px',height:'38px',
                  cursor:'pointer',fontSize:'17px',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  flexShrink:0,transition:'all 0.2s'
                }}>
                📎
              </button>

              {/* Text input — disabled when file attached */}
              <input
                value={attachFile ? '' : reply}
                onChange={e => !attachFile && setReply(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
                placeholder={attachFile ? `📎 ${attachFile.name} ready to send…` : 'Type a message...'}
                disabled={!!attachFile}
                style={{
                  flex:1,padding:'10px 14px',border:'none',borderRadius:'24px',fontSize:'13px',
                  outline:'none',background: attachFile ? '#e8f0fe' : '#fff',
                  color: attachFile ? '#1a73e8' : '#333', cursor: attachFile ? 'default' : 'text'
                }}
              />

              {/* Send button */}
              <button
                onClick={sendReply}
                disabled={sending || (!reply.trim() && !attachFile)}
                style={{
                  background:'#25d366',color:'#fff',border:'none',borderRadius:'50%',
                  width:'42px',height:'42px',cursor:'pointer',fontSize:'18px',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  flexShrink:0,opacity:sending||(!reply.trim()&&!attachFile)?0.5:1
                }}>
                {sending ? '…' : '➤'}
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}